/**
 * DocumentConsolidator Component
 * UI for merging multiple DOCX transcripts into one British English document
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  SourceDocument,
  MergeOptions,
  extractDocxContent,
  countWords,
  generateConsolidatedDocx,
} from '../../services/mergerService';
import { useAuthStore } from '../../store/useAuthStore';

type MergeStep = 'upload' | 'mapping' | 'processing' | 'complete' | 'error';

interface MergeLog {
  id: string;
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

export const DocumentConsolidator: React.FC = () => {
  const { session } = useAuthStore();
  const [documents, setDocuments] = useState<SourceDocument[]>([]);
  const [step, setStep] = useState<MergeStep>('upload');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState<MergeLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [options, setOptions] = useState<MergeOptions>({
    projectTitle: 'Consolidated Transcript',
    translateToBritishEnglish: true,
    includeSourceReferences: true,
    generateSummary: true,
  });


  // Speaker mapping: generic label -> actual name
  const [speakerMap, setSpeakerMap] = useState<Record<string, string>>({});
  const [detectedSpeakers, setDetectedSpeakers] = useState<{generic: string[], named: string[]}>({generic: [], named: []});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const addLog = useCallback((message: string, type: MergeLog['type'] = 'info') => {
    const log: MergeLog = {
      id: Math.random().toString(36).substring(2, 9),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      message,
      type,
    };
    setLogs(prev => [...prev, log]);
    
    // Auto-scroll
    setTimeout(() => {
      logContainerRef.current?.scrollTo({ top: logContainerRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  }, []);


  // Detect speakers from document content
  const detectSpeakersFromContent = useCallback((content: string): {generic: string[], named: string[]} => {
    const generic: Set<string> = new Set();
    const named: Set<string> = new Set();
    
    // Pattern for generic speakers: "Speaker 1:", "SPEAKER 2:", "Speaker_1:", etc.
    const genericPattern = /\b(Speaker[_\s]?\d+|SPEAKER[_\s]?\d+|Participant[_\s]?\d+|Person[_\s]?\d+)\s*[:\-]/gi;
    
    // Pattern for named speakers: "Name:" at start of line or after timestamp
    // Matches: "John:", "Dr. Smith:", "Mary-Jane:", but not generic patterns
    const namedPattern = /(?:^|\n|\]\s*|\d{2}:\d{2}(?::\d{2})?\s*)\s*([A-Z][a-z]+(?:[\s\-][A-Z][a-z]+)*(?:\s+[A-Z][a-z]+)?)\s*[:\-](?=\s)/gm;
    
    let match;
    
    // Find generic speakers
    while ((match = genericPattern.exec(content)) !== null) {
      const speaker = match[1].replace(/[_\s]+/g, ' ').trim();
      generic.add(speaker);
    }
    
    // Find named speakers
    while ((match = namedPattern.exec(content)) !== null) {
      const name = match[1].trim();
      // Filter out common false positives
      const skipWords = ['The', 'This', 'That', 'Here', 'There', 'What', 'When', 'Where', 'How', 'Why', 'Yes', 'No', 'Not', 'And', 'But', 'Source', 'Note', 'See'];
      if (!skipWords.includes(name) && !name.match(/^Speaker/i) && name.length > 1) {
        named.add(name);
      }
    }
    
    return {
      generic: Array.from(generic).sort(),
      named: Array.from(named).sort()
    };
  }, []);

  // Update detected speakers when documents change
  const updateDetectedSpeakers = useCallback((docs: SourceDocument[]) => {
    const allGeneric: Set<string> = new Set();
    const allNamed: Set<string> = new Set();
    
    for (const doc of docs) {
      const detected = detectSpeakersFromContent(doc.content);
      detected.generic.forEach(s => allGeneric.add(s));
      detected.named.forEach(s => allNamed.add(s));
    }
    
    setDetectedSpeakers({
      generic: Array.from(allGeneric).sort(),
      named: Array.from(allNamed).sort()
    });
    
    // Auto-populate speaker map if we have named speakers
    const namedArray = Array.from(allNamed);
    const genericArray = Array.from(allGeneric).sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)?.[0] || '0');
      const numB = parseInt(b.match(/\d+/)?.[0] || '0');
      return numA - numB;
    });
    
    if (namedArray.length > 0 && genericArray.length > 0) {
      const newMap: Record<string, string> = {};
      genericArray.forEach((generic, i) => {
        if (i < namedArray.length) {
          newMap[generic] = namedArray[i];
        }
      });
      setSpeakerMap(newMap);
    }
  }, [detectSpeakersFromContent]);

  // Update speaker detection when documents change
  React.useEffect(() => {
    if (documents.length > 0) {
      updateDetectedSpeakers(documents);
    }
  }, [documents, updateDetectedSpeakers]);

  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const validFiles = files.filter(f => 
      f.name.toLowerCase().endsWith('.docx') || 
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );

    if (validFiles.length !== files.length) {
      addLog('Some files were skipped (only .docx supported)', 'warning');
    }

    for (const file of validFiles) {
      try {
        addLog(`Extracting content from ${file.name}...`, 'info');
        const content = await extractDocxContent(file);
        const wordCount = countWords(content);
        
        const doc: SourceDocument = {
          id: Math.random().toString(36).substring(2, 9),
          filename: file.name,
          content,
          wordCount,
          extractedAt: new Date(),
        };
        
        setDocuments(prev => [...prev, doc]);
        addLog(`✓ ${file.name} loaded (${wordCount.toLocaleString()} words)`, 'success');
      } catch (err) {
        addLog(`Failed to extract ${file.name}: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      }
    }
  }, [addLog]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await processFiles(files);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [processFiles]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const removeDocument = useCallback((id: string) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  }, []);


  // Split text into chunks of approximately n words
  const splitIntoChunks = (text: string, wordsPerChunk: number): string[] => {
    const words = text.split(/\s+/);
    const chunks: string[] = [];
    for (let i = 0; i < words.length; i += wordsPerChunk) {
      chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
    }
    return chunks;
  };

  // Translate a single chunk via API
  const translateChunk = useCallback(async (
    content: string,
    filename: string,
    chunkIndex: number,
    totalChunks: number
  ): Promise<string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch('/api/intelligence', {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'translate-chunk', content, filename, chunkIndex, totalChunks }),
    });

    if (!response.ok) {
      let errorMessage = 'Translation failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = `Server error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result.translated;
  }, [session]);

  const handleMerge = useCallback(async () => {
    if (documents.length === 0) return;

    setStep('processing');
    setProgress(5);
    setError(null);
    addLog('Starting document consolidation...', 'info');

    const WORDS_PER_CHUNK = 2500;

    try {
      // Calculate total chunks for progress tracking
      let totalChunks = 0;
      const docChunks: { filename: string; chunks: string[] }[] = [];
      
      for (const doc of documents) {
        const wordCount = doc.content.split(/\s+/).length;
        const chunks = wordCount <= WORDS_PER_CHUNK 
          ? [doc.content] 
          : splitIntoChunks(doc.content, WORDS_PER_CHUNK);
        docChunks.push({ filename: doc.filename, chunks });
        totalChunks += chunks.length;
      }

      addLog(`Processing ${documents.length} documents (${totalChunks} chunks)...`, 'info');
      setProgress(10);

      // Process all chunks
      const processedDocs: string[] = [];
      let completedChunks = 0;

      for (const docChunk of docChunks) {
        const translatedChunks: string[] = [];
        addLog(`Translating: ${docChunk.filename}...`, 'info');

        for (let i = 0; i < docChunk.chunks.length; i++) {
          const translated = await translateChunk(
            docChunk.chunks[i],
            docChunk.filename,
            i,
            docChunk.chunks.length
          );
          translatedChunks.push(translated);
          completedChunks++;
          
          const translationProgress = 10 + Math.floor((completedChunks / totalChunks) * 70);
          setProgress(translationProgress);

          if (i < docChunk.chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }

        processedDocs.push(`[SOURCE: ${docChunk.filename}]\n\n${translatedChunks.join('\n\n')}`);
        addLog(`? ${docChunk.filename} translated`, 'success');
      }

      // Apply speaker mappings
      let mergedContent = processedDocs.join('\n\n---\n\n');
      
      // Replace generic speaker labels with mapped names
      if (Object.keys(speakerMap).length > 0) {
        for (const [generic, name] of Object.entries(speakerMap)) {
          if (name && name.trim()) {
            // Match variations: "Speaker 1:", "Speaker 1 :", "SPEAKER 1:", etc.
            const pattern = new RegExp(generic.replace(/\s+/g, '[\\s_]*') + '\\s*[:\\-]', 'gi');
            mergedContent = mergedContent.replace(pattern, name.trim() + ':');
            addLog(`Mapped "${generic}" ? "${name.trim()}"`, 'info');
          }
        }
      }
      addLog('All documents translated successfully', 'success');

      let summary: string | null = null;
      if (options.generateSummary) {
        setProgress(82);
        addLog('Generating executive summary...', 'info');
        try {
          summary = await translateChunk(
            mergedContent.substring(0, 12000),
            'summary',
            0,
            1
          );
        } catch {
          addLog('Summary generation skipped', 'warning');
        }
      }

      setProgress(88);
      addLog('Generating consolidated DOCX...', 'info');

      await generateConsolidatedDocx(
        documents,
        mergedContent,
        summary,
        options
      );

      setProgress(100);
      addLog('? Consolidated document downloaded!', 'success');
      setStep('complete');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      addLog(`Error: ${message}`, 'error');
      setStep('error');
    }
  }, [documents, options, addLog, speakerMap, translateChunk]);

  const handleReset = useCallback(() => {
    setDocuments([]);
    setStep('upload');
    setProgress(0);
    setLogs([]);
    setError(null);
    setOptions({
      projectTitle: 'Consolidated Transcript',
      translateToBritishEnglish: true,
      includeSourceReferences: true,
      generateSummary: true,
    });
  }, []);

  const totalWords = documents.reduce((sum, d) => sum + d.wordCount, 0);
  const isProcessing = step === 'processing';

  return (
    <div className="space-y-8">
      {/* Fixed Progress Bar at Top */}
      {isProcessing && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-lg border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                Consolidating Documents
              </span>
              <span className="text-xs font-mono text-[#00d4b8]">
                {progress}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00bfa8] to-[#00d4b8] transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-wider">
          Document <span className="text-[#00d4b8]">Consolidator</span>
        </h2>
        <p className="text-sm text-gray-500 mt-2">
          Merge multiple transcripts into one British English document
        </p>
      </div>

      {/* Upload Section */}
      <section className="bg-white border border-gray-200 rounded-[32px] p-8 shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.3em]">
            Source Documents
          </h3>
          <span className="text-[10px] font-bold text-[#00d4b8]">
            {documents.length} files • {totalWords.toLocaleString()} words
          </span>
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDragEnter={handleDragOver}
          onClick={() => !isProcessing && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
            isProcessing ? 'opacity-50 cursor-not-allowed border-gray-200' :
            isDragging ? 'border-[#00d4b8] bg-[#00d4b8]/5 scale-[1.02]' :
            'border-gray-200 hover:border-[#00d4b8]/40 hover:bg-[#00d4b8]/5'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isProcessing}
          />
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm font-bold text-gray-600">Click or drag DOCX files here</p>
          <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">Multiple files supported</p>
        </div>

        {/* Document list */}
        {documents.length > 0 && (
          <div className="mt-6 space-y-2">
            {documents.map((doc, idx) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl group border border-gray-200"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#00d4b8] text-[#0d1117] flex items-center justify-center text-xs font-black">
                    {idx + 1}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{doc.filename}</p>
                    <p className="text-[10px] text-gray-400">{doc.wordCount.toLocaleString()} words</p>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  disabled={isProcessing}
                  className="p-2 text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-0"
                  aria-label={`Remove ${doc.filename}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Options Section */}
      <section className="bg-white border border-gray-200 rounded-[32px] p-8 shadow-xl">
        <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.3em] mb-6">
          Merge Options
        </h3>

        <div className="space-y-4">
          {/* Project title */}
          <div>
            <label htmlFor="project-title" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Project Title
            </label>
            <input
              id="project-title"
              type="text"
              value={options.projectTitle}
              onChange={(e) => setOptions(prev => ({ ...prev, projectTitle: e.target.value }))}
              disabled={isProcessing}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-[#00d4b8] focus:ring-2 focus:ring-[#00d4b8]/40 outline-none disabled:opacity-50"
              placeholder="Enter project name..."
            />
          </div>

          {/* Toggle options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
              options.translateToBritishEnglish ? 'bg-[#00d4b8]/10 border-[#00d4b8]/30' : 'bg-white border-gray-200'
            }`}>
              <input
                type="checkbox"
                checked={options.translateToBritishEnglish}
                onChange={(e) => setOptions(prev => ({ ...prev, translateToBritishEnglish: e.target.checked }))}
                disabled={isProcessing}
                className="w-4 h-4 accent-[#00d4b8] rounded"
              />
              <div>
                <p className="text-[11px] font-bold text-gray-800">British English</p>
                <p className="text-[9px] text-gray-400">Translate all content</p>
              </div>
            </label>

            <label className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
              options.generateSummary ? 'bg-[#00d4b8]/10 border-[#00d4b8]/30' : 'bg-white border-gray-200'
            }`}>
              <input
                type="checkbox"
                checked={options.generateSummary}
                onChange={(e) => setOptions(prev => ({ ...prev, generateSummary: e.target.checked }))}
                disabled={isProcessing}
                className="w-4 h-4 accent-[#00d4b8] rounded"
              />
              <div>
                <p className="text-[11px] font-bold text-gray-800">Executive Summary</p>
                <p className="text-[9px] text-gray-400">AI-generated overview</p>
              </div>
            </label>

            <label className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
              options.includeSourceReferences ? 'bg-[#00d4b8]/10 border-[#00d4b8]/30' : 'bg-white border-gray-200'
            }`}>
              <input
                type="checkbox"
                checked={options.includeSourceReferences}
                onChange={(e) => setOptions(prev => ({ ...prev, includeSourceReferences: e.target.checked }))}
                disabled={isProcessing}
                className="w-4 h-4 accent-[#00d4b8] rounded"
              />
              <div>
                <p className="text-[11px] font-bold text-gray-800">Source References</p>
                <p className="text-[9px] text-gray-400">Mark document origins</p>
              </div>
            </label>
          </div>
        </div>
      </section>


      {/* Speaker Mapping Section */}
      {documents.length > 0 && detectedSpeakers.generic.length > 0 && (
        <section className="bg-amber-500/5 border border-amber-500/25 rounded-[32px] p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-amber-500/15 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-[11px] font-black text-gray-700 uppercase tracking-[0.2em]">
                Speaker Mapping
              </h3>
              <p className="text-[9px] text-gray-500">
                {detectedSpeakers.named.length > 0 
                  ? `Found ${detectedSpeakers.named.length} named speaker(s) - auto-mapped below`
                  : 'Map generic labels to actual names'}
              </p>
            </div>
          </div>

          {detectedSpeakers.named.length > 0 && (
            <div className="mb-4 p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/25">
              <p className="text-[10px] font-bold text-emerald-400 mb-1">Detected Names:</p>
              <p className="text-[10px] text-emerald-400/80">{detectedSpeakers.named.join(', ')}</p>
            </div>
          )}

          <div className="space-y-3">
            {detectedSpeakers.generic.map(generic => (
              <div key={generic} className="flex items-center gap-3">
                <span className="text-[10px] font-mono bg-gray-100 px-3 py-2 rounded-lg text-gray-500 w-32 text-center border border-gray-200">
                  {generic}
                </span>
                <span className="text-gray-400">→</span>
                <input
                  type="text"
                  value={speakerMap[generic] || ''}
                  onChange={(e) => setSpeakerMap(prev => ({ ...prev, [generic]: e.target.value }))}
                  placeholder="Enter actual name"
                  disabled={isProcessing}
                  className="flex-1 text-[11px] px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-[#00d4b8] focus:ring-1 focus:ring-[#00d4b8]/30 outline-none disabled:opacity-50"
                />
              </div>
            ))}
          </div>

          {Object.keys(speakerMap).length > 0 && Object.values(speakerMap).some(v => v) && (
            <div className="mt-4 p-3 bg-[#00d4b8]/5 rounded-xl border border-[#00d4b8]/20">
              <p className="text-[9px] text-[#00d4b8]/80">
                <strong>Note:</strong> Speaker labels will be replaced in the consolidated document.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Processing Log */}
      {logs.length > 0 && (
        <div
          ref={logContainerRef}
          className="bg-slate-950 rounded-2xl p-6 h-48 overflow-y-auto border border-slate-900"
        >
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="flex gap-4 text-[11px] font-mono">
                <span className="text-slate-600 whitespace-nowrap">[{log.timestamp}]</span>
                <span className={`${
                  log.type === 'error' ? 'text-rose-400' :
                  log.type === 'success' ? 'text-emerald-400' :
                  log.type === 'warning' ? 'text-amber-400' :
                  'text-indigo-400'
                }`}>
                  {log.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      {isProcessing && (
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#00bfa8] to-[#00d4b8] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="p-6 bg-rose-500/10 border border-rose-500/30 rounded-2xl">
          <p className="text-sm font-bold text-rose-400">{error}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleReset}
          disabled={isProcessing}
          className="flex-1 py-4 border border-gray-300 text-gray-600 font-bold rounded-2xl hover:bg-gray-100 transition-all disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={handleMerge}
          disabled={documents.length === 0 || isProcessing}
          className="flex-[2] py-4 bg-[#00d4b8] text-[#0d1117] font-black uppercase tracking-widest rounded-2xl hover:bg-[#00bfa8] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'CONSOLIDATING...' : `MERGE ${documents.length} DOCUMENT${documents.length !== 1 ? 'S' : ''}`}
        </button>
      </div>

      {/* Success state */}
      {step === 'complete' && (
        <div className="p-8 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/15 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-black text-emerald-400 uppercase tracking-wider">Consolidation Complete</h4>
          <p className="text-sm text-emerald-400/70 mt-2">Your document has been downloaded</p>
        </div>
      )}
    </div>
  );
};
